import React, { useEffect, useState } from 'react';
import { Alert, Modal, Spin } from 'antd';
import { primaryFontFamily, canonicalFamilyName } from 'admin/components/common/AI/Chat/fonts/font-resolver';
import type { SCFontItem } from 'types/fonts';
import t from 'utils/translate';
import TextPresetPlatformLabel from './TextPresetPlatformLabel';
import css from './TextPresetsEditor.scss';

export type TextPresetPreviewFont = Pick<SCFontItem, 'fontFamily'> &
	Partial<Pick<SCFontItem, 'url' | 'fontType' | 'weight' | 'style'>>;

type Sample = { key: string; label: string; referenceWidth?: number; style: React.CSSProperties };

/** Mounted only while preview is open; font registrations belong to this dialog. */
export default function TextPresetPreview({
	name,
	fonts,
	samples,
	onClose,
	colorScheme,
}: {
	name: string;
	fonts: TextPresetPreviewFont[];
	samples: Sample[];
	onClose: () => void;
	colorScheme: 'light' | 'dark';
}) {
	const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
	useEffect(() => {
		let active = true;
		const faces: FontFace[] = [];
		const links: HTMLLinkElement[] = [];
		setStatus('loading');
		const families = new Set(
			samples.map(sample => canonicalFamilyName(primaryFontFamily(sample.style.fontFamily ?? '') ?? ''))
		);
		const selected = fonts.filter(
			font => font.url && families.has(canonicalFamilyName(primaryFontFamily(font.fontFamily) ?? ''))
		);
		const load = async () => {
			await Promise.all(
				selected.map(async font => {
					if (font.fontType === 'google') {
						await new Promise<void>((resolve, reject) => {
							const link = document.createElement('link');
							link.rel = 'stylesheet';
							// Stored Google entries use either legacy variants or CSS2 axis syntax.
							link.href = `https://fonts.googleapis.com/${font.url!.includes('@') ? 'css2' : 'css'}?family=${font.url}&display=swap`;
							link.onload = () => resolve();
							link.onerror = () => reject(new Error('Font stylesheet failed to load.'));
							links.push(link);
							document.head.appendChild(link);
						});
					} else {
						const face = new FontFace(
							primaryFontFamily(font.fontFamily)!,
							`url(${JSON.stringify(font.url)})`,
							{
								weight: String(font.weight || 400),
								style: font.style?.replace(/['"]+/g, '').toLowerCase() || 'normal',
							}
						);
						await face.load();
						if (active) {
							document.fonts.add(face);
							faces.push(face);
						}
					}
				})
			);
			if (!active) return;
			await Promise.all(
				samples.map(async sample => {
					const family = primaryFontFamily(sample.style.fontFamily ?? '') ?? 'sans-serif';
					const loaded = await document.fonts.load(
						`${sample.style.fontWeight ?? 400} 16px ${JSON.stringify(family)}`,
						t('textPresets.sample')
					);
					const local =
						/^(serif|sans-serif|monospace|system-ui|cursive|fantasy|Arial|Helvetica|Verdana|Georgia|Times New Roman|Courier New)$/i.test(
							family
						);
					if (!loaded.length && !local) throw new Error('Preview font is unavailable.');
				})
			);
			if (active) setStatus('ready');
		};
		load().catch(() => {
			if (active) setStatus('error');
		});
		return () => {
			active = false;
			links.forEach(link => link.remove());
			faces.forEach(face => document.fonts.delete(face));
		};
	}, [fonts, samples]);
	return (
		<Modal
			open
			title={
				<span className={css.previewTitle}>
					<span>{name}</span>
					<span className={css.previewCaption}>{t('textPresets.preview')}</span>
				</span>
			}
			onCancel={onClose}
			footer={null}
			width={960}
		>
			{status === 'loading' ? (
				<div className={css.previewLoading}>
					<Spin />
					<span>{t('textPresets.loadingFonts')}</span>
				</div>
			) : null}
			{status === 'error' ? <Alert type="error" showIcon title={t('textPresets.previewFontError')} /> : null}
			{status === 'ready' ? (
				<div
					className={css.previewGrid}
					data-theme={colorScheme}
					role="region"
					aria-label={t('textPresets.preview')}
					// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard scrolling
					tabIndex={0}
				>
					{samples.map(sample => (
						<section className={css.previewPlatform} key={sample.key}>
							<h3 className={css.previewHeading}>
								<TextPresetPlatformLabel
									platform={sample.key}
									label={sample.label}
									referenceWidth={sample.referenceWidth}
								/>
							</h3>
							<div className={css.preview}>
								<div style={sample.style}>{t('textPresets.sample')}</div>
							</div>
						</section>
					))}
				</div>
			) : null}
		</Modal>
	);
}
